import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, UserCog, Mail, Shield, Trash2, Eye, EyeOff, Edit2, LogIn, Key } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { clearAppCache } from '@/utils/cacheUtils';

export default function UserManagement({ user, onLogout }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [originalAdminToken, setOriginalAdminToken] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'sales'
  });

  const [editFormData, setEditFormData] = useState({
    full_name: '',
    email: '',
    role: ''
  });

  const [passwordFormData, setPasswordFormData] = useState({
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchUsers();
    // Check if we're impersonating
    const storedAdminToken = localStorage.getItem('admin_token');
    if (storedAdminToken) {
      setOriginalAdminToken(storedAdminToken);
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'sales'
    });
    setShowPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.full_name) {
      toast.error('Please fill all required fields');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/register', formData);
      await clearAppCache();
      toast.success('User created successfully!');
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      await clearAppCache();
      toast.success('User deleted successfully!');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setEditFormData({
      full_name: u.full_name,
      email: u.email,
      role: u.role
    });
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/users/${editingUser.id}`, editFormData);
      await clearAppCache();
      toast.success('User updated successfully!');
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = (u) => {
    setEditingUser(u);
    setPasswordFormData({ new_password: '', confirm_password: '' });
    setPasswordDialogOpen(true);
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (passwordFormData.new_password !== passwordFormData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordFormData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await api.put(`/users/${editingUser.id}`, { new_password: passwordFormData.new_password });
      toast.success('Password changed successfully!');
      setPasswordDialogOpen(false);
      setEditingUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginAsUser = async (u) => {
    if (!window.confirm(`Login as "${u.full_name}"? You can return back anytime.`)) return;
    
    try {
      // Store current admin token before impersonating
      const currentToken = localStorage.getItem('token');
      localStorage.setItem('admin_token', currentToken);
      
      const response = await api.post(`/users/${u.id}/impersonate`);
      
      // Set new token and user
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('impersonating', 'true');
      
      toast.success(`Now logged in as ${u.full_name}`);
      
      // Reload the page to apply new user context
      window.location.href = u.role === 'customer' ? '/customer' : '/admin';
    } catch (error) {
      toast.error('Failed to login as user');
    }
  };

  const handleBackToAdmin = () => {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      // Get admin user details from token (simplified - in real app decode JWT)
      localStorage.setItem('token', adminToken);
      localStorage.removeItem('admin_token');
      localStorage.removeItem('impersonating');
      
      toast.success('Back to Admin account');
      window.location.href = '/admin/users';
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-700 border-purple-200',
      sales: 'bg-blue-100 text-blue-700 border-blue-200',
      billing: 'bg-green-100 text-green-700 border-green-200',
      customer: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    return <Badge className={colors[role] || colors.customer}>{role}</Badge>;
  };

  const roles = [
    { value: 'admin', label: 'Admin', description: 'Full access to all modules' },
    { value: 'sales', label: 'Sales', description: 'Access to Leads, Customers' },
    { value: 'billing', label: 'Billing', description: 'Access to Billing, Customers' },
    { value: 'customer', label: 'Customer', description: 'Customer portal only' },
  ];

  const isImpersonating = localStorage.getItem('impersonating') === 'true';

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="user-management-page">
        {/* Back to Admin Banner */}
        {isImpersonating && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <p className="text-amber-800 font-medium">
                  You are viewing as another user
                </p>
                <Button
                  onClick={handleBackToAdmin}
                  className="bg-amber-600 hover:bg-amber-700 gap-2"
                >
                  <LogIn size={16} />
                  Back to Admin
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <UserCog className="text-emerald-600" />
                  User Management
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">Create and manage user accounts</p>
              </div>
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button data-testid="new-user-button" className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                    <Plus size={18} />
                    New User
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        data-testid="user-fullname-input"
                        placeholder="Enter full name"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        data-testid="user-email-input"
                        type="email"
                        placeholder="email@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password *</Label>
                      <div className="relative">
                        <Input
                          data-testid="user-password-input"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Min 6 characters"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Role *</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {roles.map((role) => (
                          <button
                            key={role.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, role: role.value })}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              formData.role === role.value
                                ? 'border-emerald-600 bg-emerald-50'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <p className="font-medium text-sm text-slate-800">{role.label}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{role.description}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 h-11">
                      {loading ? 'Creating...' : 'Create User'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((u) => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                              <span className="text-emerald-700 font-bold text-sm">
                                {u.full_name?.charAt(0) || 'U'}
                              </span>
                            </div>
                            <span className="font-medium">{u.full_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-slate-600">
                            <Mail size={14} />
                            {u.email}
                          </div>
                        </TableCell>
                        <TableCell>{getRoleBadge(u.role)}</TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {/* Edit */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditUser(u)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Edit User"
                            >
                              <Edit2 size={14} />
                            </Button>
                            
                            {/* Change Password */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleChangePassword(u)}
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              title="Change Password"
                            >
                              <Key size={14} />
                            </Button>
                            
                            {/* Login as User */}
                            {u.email !== user?.email && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleLoginAsUser(u)}
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                title="Login as this user"
                              >
                                <LogIn size={14} />
                              </Button>
                            )}
                            
                            {/* Delete */}
                            {u.email !== user?.email && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteUser(u.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete User"
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={editFormData.full_name}
                  onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Role *</Label>
                <select
                  value={editFormData.role}
                  onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-white"
                >
                  {roles.map((role) => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700">
                {loading ? 'Updating...' : 'Update User'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Change Password - {editingUser?.full_name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div className="space-y-2">
                <Label>New Password *</Label>
                <Input
                  type="password"
                  placeholder="Min 6 characters"
                  value={passwordFormData.new_password}
                  onChange={(e) => setPasswordFormData({ ...passwordFormData, new_password: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Confirm Password *</Label>
                <Input
                  type="password"
                  placeholder="Confirm password"
                  value={passwordFormData.confirm_password}
                  onChange={(e) => setPasswordFormData({ ...passwordFormData, confirm_password: e.target.value })}
                  required
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700">
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
